import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type { IProfile } from 'types/interfaces/IProfile'

const viewerKey = (
  username?: string,
  viewerId?: string | null
) =>
  username
    ? `${username.toLowerCase()}::${viewerId ?? '0'}`
    : ''

interface ProfileMediaCountIncrement {
  photo_count?: number
  video_count?: number
  premium_video_count?: number
}

interface ProfileCacheState {
  cache: Record<string, IProfile>
  updatedAt: Record<string, number>

  setProfile: (
    username: string,
    profile: IProfile,
    viewerId?: string | null
  ) => void

  getProfile: (
    username: string,
    viewerId?: string | null
  ) => IProfile | undefined

  getUpdatedAt: (
    username: string,
    viewerId?: string | null
  ) => number | undefined

  incrementMediaCounts: (
    username: string,
    counts: ProfileMediaCountIncrement,
    viewerId?: string | null
  ) => void

  clear: () => void
}

export const useProfileCacheStore =
  create<ProfileCacheState>()(
    persist(
      (set, get) => ({
        cache: {},
        updatedAt: {},

        setProfile: (username, profile, viewerId) => {
          const key = viewerKey(username, viewerId)

          if (!key) return

          const normalizedProfile: IProfile = {
            ...profile,
            photo_count: Number(
              profile.photo_count ?? 0
            ),
            video_count: Number(
              profile.video_count ?? 0
            ),
            premium_video_count: Number(
              profile.premium_video_count ?? 0
            ),
            all_media_categories: [
              ...(profile.all_media_categories ?? [])
            ],
          }

          set(state => ({
            cache: {
              ...state.cache,
              [key]: normalizedProfile,
            },
            updatedAt: {
              ...state.updatedAt,
              [key]: Date.now(),
            },
          }))
        },

        incrementMediaCounts: (
          username,
          counts,
          viewerId
        ) => {
          const key = viewerKey(username, viewerId)

          if (!key) return

          set(state => {
            const current = state.cache[key]

            if (!current) return state

            const nextProfile: IProfile = {
              ...current,
              photo_count:
                Number(current.photo_count ?? 0) +
                Number(counts.photo_count ?? 0),
              video_count:
                Number(current.video_count ?? 0) +
                Number(counts.video_count ?? 0),
              premium_video_count:
                Number(
                  current.premium_video_count ?? 0
                ) +
                Number(
                  counts.premium_video_count ?? 0
                ),
            }

            return {
              cache: {
                ...state.cache,
                [key]: nextProfile,
              },
              updatedAt: {
                ...state.updatedAt,
                [key]: Date.now(),
              },
            }
          })
        },

        getProfile: (username, viewerId) => {
          const key = viewerKey(
            username,
            viewerId
          )

          if (!key) return undefined

          return get().cache[key]
        },

        getUpdatedAt: (
          username,
          viewerId
        ) => {
          const key = viewerKey(
            username,
            viewerId
          )

          if (!key) return undefined

          return get().updatedAt[key]
        },

        clear: () =>
          set({
            cache: {},
            updatedAt: {},
          }),
      }),
      {
        name: 'profile-cache',
        partialize: state => ({
          cache: state.cache,
          updatedAt: state.updatedAt,
        }),
      }
    )
  )